"use client";

import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { CalendarDays, Car, ClipboardList, UserRound, Wrench } from "lucide-react";

import { AppDialog } from "@/components/ui/app-dialog";
import { formatVehiclePlate } from "@/lib/formatters";
import { apiClient } from "@/lib/api-client";
import { LineItemType } from "@/types/line-item-type";
import type { WorkOrder } from "@/types/work-order";
import { WorkOrderStatus } from "@/types/work-order-status";
import { ServiceExecutionStatus } from "@/types/service-execution-status";

export type WorkOrderDetailsDialogProps = {
  workOrderId: string | null;
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  backLabel?: string;
  onOpenClient?: (clientId: string) => void;
  onOpenVehicle?: (vehicleId: string) => void;
};

const statusLabel: Record<WorkOrderStatus, string> = {
  [WorkOrderStatus.Open]: "Em orçamento",
  [WorkOrderStatus.InProgress]: "Em andamento",
  [WorkOrderStatus.AwaitingParts]: "Aguardando peças",
  [WorkOrderStatus.Ready]: "Concluída",
  [WorkOrderStatus.Delivered]: "Entregue",
};

const statusClass: Record<WorkOrderStatus, string> = {
  [WorkOrderStatus.Open]: "bg-slate-100 text-slate-700",
  [WorkOrderStatus.InProgress]: "bg-blue-50 text-blue-700",
  [WorkOrderStatus.AwaitingParts]: "bg-amber-50 text-amber-700",
  [WorkOrderStatus.Ready]: "bg-emerald-50 text-emerald-700",
  [WorkOrderStatus.Delivered]: "bg-emerald-50 text-emerald-700",
};

const itemTypeLabel: Record<LineItemType, string> = {
  [LineItemType.Part]: "Peça",
  [LineItemType.Service]: "Serviço",
};

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

function resolveWorkOrderStatus(order: WorkOrder): WorkOrderStatus {
  if (order.executionStatus === ServiceExecutionStatus.Finished) {
    return WorkOrderStatus.Ready;
  }
  if (order.executionStatus === ServiceExecutionStatus.WaitingParts) {
    return WorkOrderStatus.AwaitingParts;
  }
  if (order.executionStatus === ServiceExecutionStatus.InProgress) {
    return WorkOrderStatus.InProgress;
  }
  return order.status;
}

function DetailLabel({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 rounded-lg border border-slate-100 bg-slate-50/70 p-3">
      <span className="mt-0.5 text-slate-400">{icon}</span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        <div className="mt-0.5 text-sm font-medium text-slate-800">{children}</div>
      </div>
    </div>
  );
}

export function WorkOrderDetailsDialog({
  workOrderId,
  open,
  onClose,
  onBack,
  backLabel = "Voltar",
  onOpenClient,
  onOpenVehicle,
}: WorkOrderDetailsDialogProps) {
  const workOrderQuery = useQuery({
    queryKey: ["work-order", workOrderId],
    enabled: open && Boolean(workOrderId),
    queryFn: async () => {
      const response = await apiClient.get<WorkOrder>(`/ordens-servico/${workOrderId}`);
      return response.data;
    },
  });

  const order = workOrderQuery.data;
  const status = order ? resolveWorkOrderStatus(order) : null;
  const laborCost = Number(order?.laborCost ?? 0);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={order ? `Ordem de serviço ${order.number}` : "Visualizar ordem de serviço"}
      description={order ? `Aberta em ${new Date(order.createdAt).toLocaleString("pt-BR")}` : "Dados completos da ordem selecionada"}
      size="xl"
      mobileFullscreen
      actions={
        <>
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              {backLabel}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
          >
            Fechar
          </button>
        </>
      }
    >
      {workOrderQuery.isLoading ? (
        <div className="py-12 text-center text-sm text-slate-500">Carregando ordem de serviço...</div>
      ) : workOrderQuery.isError ? (
        <div className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>Não foi possível carregar os dados desta ordem.</p>
          <button
            type="button"
            onClick={() => workOrderQuery.refetch()}
            className="font-semibold underline underline-offset-2"
          >
            Tentar novamente
          </button>
        </div>
      ) : order && status ? (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total da ordem</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">
                {currencyFormatter.format(Number(order.total))}
              </p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClass[status]}`}>
              {statusLabel[status]}
            </span>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <DetailLabel icon={<UserRound className="h-4 w-4" />} label="Cliente">
              {onOpenClient ? (
                <button type="button" onClick={() => onOpenClient(order.client.id)} className="text-left font-semibold underline-offset-2 hover:text-[#E8441A] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8441A]" aria-label={`Abrir cliente ${order.client.name}`}>
                  {order.client.name}
                </button>
              ) : order.client.name}
            </DetailLabel>
            <DetailLabel icon={<Car className="h-4 w-4" />} label="Veículo">
              {onOpenVehicle ? (
                <button type="button" onClick={() => onOpenVehicle(order.vehicle.id)} className="block max-w-full truncate text-left font-semibold underline-offset-2 hover:text-[#E8441A] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8441A]" aria-label={`Abrir veículo ${order.vehicle.plate}`}>
                  {formatVehiclePlate(order.vehicle.plate)} · {order.vehicle.brand} {order.vehicle.model}
                </button>
              ) : <span className="block truncate">{formatVehiclePlate(order.vehicle.plate)} · {order.vehicle.brand} {order.vehicle.model}</span>}
            </DetailLabel>
            <DetailLabel icon={<Wrench className="h-4 w-4" />} label="Mecânico responsável">
              {order.assignedTo?.fullName ?? "Não atribuído"}
            </DetailLabel>
            <DetailLabel icon={<CalendarDays className="h-4 w-4" />} label="Previsão de entrega">
              {order.estimatedDelivery
                ? new Date(order.estimatedDelivery).toLocaleDateString("pt-BR", { timeZone: "UTC" })
                : "Não informada"}
            </DetailLabel>
          </div>

          {order.notes ? (
            <section className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-slate-400" />
                <h3 className="text-sm font-semibold text-slate-800">Observações</h3>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-600">{order.notes}</p>
            </section>
          ) : null}

          <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <h3 className="text-sm font-semibold text-slate-800">Itens da ordem</h3>
              <span className="text-xs text-slate-500">{order.items.length} item(ns)</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Descrição</th>
                    <th className="px-4 py-3">Tipo</th>
                    <th className="px-4 py-3 text-right">Qtd.</th>
                    <th className="px-4 py-3 text-right">Unitário</th>
                    <th className="px-4 py-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {order.items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3 font-medium text-slate-800">{item.description}</td>
                      <td className="px-4 py-3 text-slate-600">{itemTypeLabel[item.type]}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{item.quantity}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{currencyFormatter.format(Number(item.unitPrice))}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-800">{currencyFormatter.format(Number(item.total))}</td>
                    </tr>
                  ))}
                  {laborCost > 0 ? (
                    <tr className="bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">Mão de obra</td>
                      <td className="px-4 py-3 text-slate-600">Serviço</td>
                      <td className="px-4 py-3 text-right text-slate-600">—</td>
                      <td className="px-4 py-3 text-right text-slate-600">—</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-800">{currencyFormatter.format(laborCost)}</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {order.items.length === 0 && laborCost <= 0 ? (
              <p className="px-4 py-5 text-sm text-slate-500">Nenhum item lançado nesta ordem.</p>
            ) : null}
          </section>
        </div>
      ) : null}
    </AppDialog>
  );
}
